import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, doc, updateDoc, query, where, orderBy, onSnapshot, getDocs } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Table, Box, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Typography, Container, CircularProgress, Snackbar, Alert, Chip
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DialogActions from "@mui/material/DialogActions";
import PhotoThumbnail from '../components/PhotoThumbnail';
import VisitorCheckoutQR from '../components/VisitorCheckoutQR';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';

const VisitApprovals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { isSuperAdmin, isAppAdmin } = useAuth();
  const [qrDialog, setQrDialog] = useState({ open: false, visitor: null });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // DEBUG: Log all host users and their fields
    (async () => {
      const q = query(collection(db, "users"), where("role", "==", "host"));
      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        console.log("[DEBUG] Host user doc:", doc.id, doc.data());
      });
    })();

    // Debug: Log user context
    console.log("[DEBUG] VisitApprovals: user:", user);
    console.log("[DEBUG] VisitApprovals: isAppAdmin:", isAppAdmin());
    console.log("[DEBUG] VisitApprovals: isSuperAdmin:", isSuperAdmin());
    console.log("[DEBUG] VisitApprovals: userEmailID:", user.userEmailID);
    console.log("[DEBUG] VisitApprovals: userRole:", user.role);

    // Restrict superadmin from accessing pending approvals
    if (isSuperAdmin()) {
      setVisitors([]);
      setLoading(false);
      return;
    }

    const fetchVisitApprovals = async () => {
      try {
        let q;

        if (isAppAdmin()) {
          // AppAdmin: Show approvals for their tenant's hosts ONLY
          // Extra validation and debug logs for tenant/host mapping
          const tenantQuery = query(
            collection(db, "tenantmaster"),
            where("appAdminEmail", "==", (user.userEmailID || user.email).toLowerCase())
          );
          const tenantSnapshot = await getDocs(tenantQuery);

          // Debug: Log tenant query result
          console.log("[DEBUG] VisitApprovals: tenantQuery result:", tenantSnapshot.docs.map(doc => doc.data()));

          if (!tenantSnapshot.empty) {
            const tenantData = tenantSnapshot.docs[0].data();
            if (!tenantData.tenantId) {
              console.error("Tenant data missing tenantId:", tenantData);
              setVisitors([]);
              setLoading(false);
              return;
            }
            // Fetch hosts for this tenant
            const hostsQuery = query(
              collection(db, "users"),
              where("tenantId", "==", tenantData.tenantId),
              where("role", "==", "host")
            );
            const hostsSnapshot = await getDocs(hostsQuery);
            // Debug: Log hosts found
            console.log("[DEBUG] VisitApprovals: hosts found:", hostsSnapshot.docs.map(doc => doc.data()));
            const hostIds = hostsSnapshot.docs.map(doc => doc.data().userID || doc.id);
            // Debug: Log hostIds array
            console.log("[DEBUG] VisitApprovals: hostIds array:", hostIds);
            if (hostIds.length === 0) {
              console.warn("No hosts found for tenantId:", tenantData.tenantId);
              setVisitors([]);
              setLoading(false);
              return;
            }
            // Debug log for hostIds
            console.log("AppAdmin hostIds for tenant (using userID):", hostIds);
            // Debug: Log Firestore query params
            console.log("[DEBUG] VisitApprovals: Firestore query params:", {
              collection: "visit_approvals",
              hostUserID_in: hostIds,
              status: "pending"
            });
            q = query(
              collection(db, "visit_approvals"),
              where("hostUserID", "in", hostIds),
              where("status", "==", "pending"),
              orderBy("createdAt", "desc")
            );
          } else {
            console.warn("No tenant found for AppAdmin email:", user.userEmailID || user.email);
            setVisitors([]);
            setLoading(false);
            return;
          }
        } else {
          // Regular host: Show their own approvals and those for their tenant
          // Host: show only approvals assigned to this host
          // Host approvals for tenant (handles residential + commercial)
          // Host approvals (Residential + Commercial)
          q = query(
            collection(db, "visit_approvals"),
            where("status", "==", "pending"),
            orderBy("createdAt", "desc")
          );
        }

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const visitorData = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate()?.toLocaleString() || "N/A"
              }))
              .filter(v => {
                const facilityType = v.facilityType;
                // Residential facility
                if (facilityType === 100) {
                  return v.hostUserID === (user.userID || user.uid);
                }

                // Commercial facility
                if (facilityType === 200) {
                  return v.facilityId === user.facilityId || v.tenantId === user.tenantId;
                }
                return false;
              });
            // Debug: Log fetched visitor approvals
            console.log("[DEBUG] VisitApprovals: visitor approvals fetched:", visitorData);
            setVisitors(visitorData);
            setLoading(false);
          },
          (error) => {
            console.error("Error fetching visitors:", error);
            setSnackbar({
              open: true,
              message: "Error loading visitors. Please try again.",
              severity: "info"
            });
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error("Error setting up visit approvals query:", error);
        setLoading(false);
      }
    };

    fetchVisitApprovals();
  }, [user, navigate, isSuperAdmin, isAppAdmin]);

  const approveVisitor = async (id) => {
    try {
      setApprovingId(id);
      const visitorRef = doc(db, "visit_approvals", id);
      await updateDoc(visitorRef, {
        status: "approved",
        approvedBy: user.userEmailID || user.email || user.uid,
        approvedAt: new Date(),
      });

      setSnackbar({
        open: true,
        message: "Visitor approved successfully!",
        severity: "success"
      });
    } catch (error) {
      console.error("Error approving visitor:", error);
      setSnackbar({
        open: true,
        message: "Failed to approve visitor.",
        severity: "info"
      });
    } finally {
      setApprovingId(null);
    }
  };

  const rejectVisitor = async (id) => {
    try {
      const visitorRef = doc(db, "visit_approvals", id);

      await updateDoc(visitorRef, {
        status: "rejected",
        rejectedBy: user.userEmailID || user.email || user.uid,
        rejectedAt: new Date(),
      });

      setSnackbar({
        open: true,
        message: "Visitor rejected successfully!",
        severity: "warning",
      });
    } catch (error) {
      console.error("Error rejecting visitor:", error);
      setSnackbar({
        open: true,
        message: "Failed to reject visitor.",
        severity: "error",
      });
    }
  };

  const handleCall = async (visitor) => {
    try {
      const operatorQuery = query(
        collection(db, "users"),
        where("tenantId", "==", visitor.tenantId),
        where("role", "==", "appoperator")
      );

      const snapshot = await getDocs(operatorQuery);

      if (snapshot.empty) {
        setSnackbar({
          open: true,
          message: "Facility operator number not available",
          severity: "warning"
        });
        return;
      }

      const operator = snapshot.docs[0].data();
      const phone = operator.userMobileNo || operator.phone;

      if (!phone) {
        setSnackbar({
          open: true,
          message: "Facility operator number missing",
          severity: "warning"
        });
        return;
      }

      window.location.href = `tel:${phone}`;

    } catch (err) {
      console.error("Call error:", err);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: "2rem",
            fontWeight: 900,
            fontFamily: "'Unbounded', sans-serif",
            color: "#1c1400"
          }}>
            Visitor Approvals
          </Typography>

          <Typography sx={{ color: "#8a7a40" }}>
            Review and approve visitor requests
          </Typography>
        </Box>

        {/* LOADING */}
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : visitors.length === 0 ? (

          /* EMPTY STATE */
          <Paper sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            border: "1px solid #f0d060",
            background: "#fffbea"
          }}>
            <Typography sx={{ color: "#6b7280" }}>
              No visitors pending approval.
            </Typography>
          </Paper>

        ) : (

          /* TABLE CARD */
          <Paper sx={{
            borderRadius: "16px",
            border: "1px solid #f0d060",
            overflow: "hidden",
            background: "#ffffff"
          }}>

            <TableContainer>

              <Table>

                {/* HEADER */}
                <TableHead>
                  <TableRow sx={{ background: "#fef3c7" }}>
                    <TableCell><b>Photo</b></TableCell>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>Mobile</b></TableCell>
                    <TableCell><b>Purpose</b></TableCell>
                    <TableCell><b>Created</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>

                {/* BODY */}
                <TableBody>
                  {visitors.map((visitor) => (
                    <TableRow
                      key={visitor.id}
                      sx={{
                        transition: "0.2s",
                        "&:hover": {
                          background: "#fffbea"
                        }
                      }}
                    >

                      {/* PHOTO */}
                      <TableCell>
                        <PhotoThumbnail
                          photoUrl={visitor.photoUrl}
                          visitorName={visitor.visitorName}
                          visitorUserID={visitor.visitorUserID}
                          visitorEmail={visitor.visitorEmail}
                          size={40}
                          variant="circular"
                          showZoom
                          showCamera={false}
                        />
                      </TableCell>

                      {/* NAME */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {visitor.visitorName}
                        </Typography>
                      </TableCell>

                      {/* EMAIL */}
                      <TableCell sx={{ color: "#6b7280" }}>
                        {visitor.visitorEmail}
                      </TableCell>

                      {/* MOBILE */}
                      <TableCell>{visitor.visitorMobileNo}</TableCell>

                      {/* PURPOSE */}
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography sx={{ fontSize: "0.9rem" }}>
                          {visitor.visitReason}
                        </Typography>
                      </TableCell>

                      {/* CREATED */}
                      <TableCell sx={{ color: "#6b7280" }}>
                        {visitor.createdAt}
                      </TableCell>

                      {/* STATUS */}
                      <TableCell>
                        <Chip
                          label={visitor.status}
                          sx={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                            background:
                              visitor.status === "approved" ? "#dcfce7" :
                                visitor.status === "rejected" ? "#fee2e2" :
                                  visitor.status === "pending" ? "#fef3c7" :
                                    "#e0f2fe",
                            color:
                              visitor.status === "approved" ? "#166534" :
                                visitor.status === "rejected" ? "#991b1b" :
                                  visitor.status === "pending" ? "#92400e" :
                                    "#075985"
                          }}
                        />
                      </TableCell>

                      {/* ACTIONS */}
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>

                          {/* CALL */}
                          {(visitor.facilityType || visitor.tenantType) === 100 && (
                            <IconButton
                              onClick={() => handleCall(visitor)}
                              sx={{
                                background: "#e0f2fe",
                                "&:hover": { background: "#bae6fd" }
                              }}
                            >
                              <PhoneIcon sx={{ color: "#0284c7" }} />
                            </IconButton>
                          )}

                          {/* APPROVE */}
                          <IconButton
                            onClick={() => approveVisitor(visitor.id)}
                            disabled={
                              approvingId === visitor.id ||
                              visitor.status === "approved" ||
                              visitor.status === "rejected"
                            }
                            sx={{
                              background: "#dcfce7",
                              "&:hover": { background: "#bbf7d0" }
                            }}
                          >
                            {approvingId === visitor.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckCircleIcon sx={{ color: "#16a34a" }} />
                            )}
                          </IconButton>

                          {/* REJECT */}
                          <IconButton
                            onClick={() => rejectVisitor(visitor.id)}
                            disabled={
                              visitor.status === "approved" ||
                              visitor.status === "rejected"
                            }
                            sx={{
                              background: "#fee2e2",
                              "&:hover": { background: "#fecaca" }
                            }}
                          >
                            <CancelIcon sx={{ color: "#dc2626" }} />
                          </IconButton>

                        </Box>
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>

              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default VisitApprovals;
